import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import AdminLayout from '../../../../components/Admin/Layout/AdminLayout';
import ModuleLayout from '../../../../layouts/ModuleLayout';
import Pagination from '../../../../components/shared/Pagination';
import {
  Download,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  Search,
  SlidersHorizontal,
  ChevronUp,
  ChevronDown,
  Loader2,
  AlertCircle,
  Calendar
} from 'lucide-react';

// ============================================
// REUSABLE REPORT LAYOUT COMPONENT
// ============================================

/**
 * ReportLayout - A comprehensive layout component for all reports
 *
 * Props:
 * - title: Report title
 * - description: Report description
 * - icon: Lucide icon component
 * - color: Theme color (teal, emerald, blue, purple, amber)
 * - summaryCards: Array of summary card objects
 * - columns: Array of table column definitions
 * - data: Array of table data
 * - filters: React node for custom filters
 * - loading: Boolean for loading state
 * - error: Error message string
 * - pagination: Pagination props { currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }
 * - onExport: Function(format) - 'csv' | 'excel' | 'pdf'
 * - onRefresh: Function to refresh data
 * - onSearch: Function for search
 * - searchPlaceholder: Placeholder text for search
 */

// Color schemes for reports
const colorSchemes = {
  teal: {
    primary: 'teal-600',
    light: 'teal-50',
    border: 'teal-200',
    text: 'teal-700',
    gradient: 'from-teal-500 to-teal-600'
  },
  emerald: {
    primary: 'emerald-600',
    light: 'emerald-50',
    border: 'emerald-200',
    text: 'emerald-700',
    gradient: 'from-emerald-500 to-emerald-600'
  },
  blue: {
    primary: 'blue-600',
    light: 'blue-50',
    border: 'blue-200',
    text: 'blue-700',
    gradient: 'from-blue-500 to-blue-600'
  },
  purple: {
    primary: 'purple-600',
    light: 'purple-50',
    border: 'purple-200',
    text: 'purple-700',
    gradient: 'from-purple-500 to-purple-600'
  },
  amber: {
    primary: 'amber-600',
    light: 'amber-50',
    border: 'amber-200',
    text: 'amber-700',
    gradient: 'from-amber-500 to-amber-600'
  }
};

// Summary Card Component
export function SummaryCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'teal' }) {
  const isPositive = trend === 'up';
  const isNegative = trend === 'down';

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
          {trendValue && (
            <div className={`flex items-center mt-2 text-xs font-medium ${
              isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-500'
            }`}>
              {isPositive && <ChevronUp size={14} />}
              {isNegative && <ChevronDown size={14} />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        {Icon && (
          <div className={`w-10 h-10 bg-${color}-50 rounded-lg flex items-center justify-center`}>
            <Icon size={20} className={`text-${color}-600`} />
          </div>
        )}
      </div>
    </div>
  );
}

// Date Range Picker Component
export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onPresetSelect,
  selectedPreset
}) {
  const presets = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'thisWeek' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
  ];

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
        {presets.map(preset => (
          <button
            key={preset.value}
            onClick={() => onPresetSelect?.(preset.value)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              selectedPreset === preset.value
                ? 'bg-white text-teal-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative">
          <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange?.(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
        <span className="text-gray-400">to</span>
        <div className="relative">
          <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange?.(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>
    </div>
  );
}

// Data Table Component with Sorting
export function DataTable({
  columns,
  data,
  sortColumn,
  sortDirection,
  onSort,
  loading,
  emptyMessage = 'No data available'
}) {
  const handleSort = (columnKey) => {
    if (!onSort) return;
    if (sortColumn === columnKey) {
      onSort(columnKey, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(columnKey, 'asc');
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                  col.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                onClick={() => col.sortable && handleSort(col.key)}
                style={{ width: col.width }}
              >
                <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''}`}>
                  <span>{col.label}</span>
                  {col.sortable && (
                    <div className="flex flex-col">
                      <ChevronUp
                        size={12}
                        className={`-mb-1 ${sortColumn === col.key && sortDirection === 'asc' ? 'text-teal-600' : 'text-gray-300'}`}
                      />
                      <ChevronDown
                        size={12}
                        className={`-mt-1 ${sortColumn === col.key && sortDirection === 'desc' ? 'text-teal-600' : 'text-gray-300'}`}
                      />
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center">
                <Loader2 size={32} className="animate-spin text-teal-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading data...</p>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center">
                <AlertCircle size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">{emptyMessage}</p>
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={row.id || rowIndex}
                className="hover:bg-gray-50 transition-colors"
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-sm ${
                      col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''
                    }`}
                  >
                    {col.render ? col.render(row[col.key], row, rowIndex) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Export Dropdown Component
export function ExportDropdown({ onExport, loading }) {
  const [isOpen, setIsOpen] = useState(false);

  const exportOptions = [
    { format: 'csv', label: 'Export as CSV', icon: FileText, description: 'Raw data format' },
    // { format: 'excel', label: 'Export as Excel', icon: FileSpreadsheet, description: 'Formatted spreadsheet' },
    // { format: 'pdf', label: 'Export as PDF', icon: FileText, description: 'Print-ready document' },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        <span>Export</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
            {exportOptions.map((option) => (
              <button
                key={option.format}
                onClick={() => {
                  onExport?.(option.format);
                  setIsOpen(false);
                }}
                className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
              >
                <option.icon size={18} className="text-gray-500 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{option.label}</p>
                  <p className="text-xs text-gray-500">{option.description}</p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Filter Panel Component
export function FilterPanel({ children, isExpanded, onToggle }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

// Status Badge Component
export function StatusBadge({ status, type = 'default' }) {
  const statusStyles = {
    success: 'bg-green-100 text-green-700 border-green-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    danger: 'bg-red-100 text-red-700 border-red-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
    default: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusStyles[type]}`}>
      {status}
    </span>
  );
}

// Progress Bar Component
export function ProgressBar({ value, max = 100, color = 'teal', showLabel = true }) {
  const percentage = Math.min((value / max) * 100, 100);

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full bg-${color}-500 rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-xs font-medium text-gray-600 w-12 text-right">
          {percentage.toFixed(0)}%
        </span>
      )}
    </div>
  );
}

// Main Report Layout Component
export default function ReportLayout({
  title,
  description,
  icon: Icon,
  color = 'teal',
  category,        // Category name e.g., "Sales", "Financial"
  categoryId,      // Category ID for navigation e.g., "sales", "financial"
  summaryCards = [],
  columns = [],
  data = [],
  filters,
  loading = false,
  error,
  pagination,
  onExport,
  onRefresh,
  onSearch,
  searchPlaceholder = 'Search...',
  sortColumn,
  sortDirection,
  onSort,
  children,
  showFilters = true,
  showSearch = true,
  showExport = true,
  exportPermission,    // Permission code to gate export button (e.g. P.REPORTS.SALES.DAILY_EXPORT)
}) {
  const location = useLocation();
  const { hasPermission } = useAuth();

  // If exportPermission is provided, check it; otherwise allow export
  const canExport = !exportPermission || hasPermission(exportPermission);
  const colors = colorSchemes[color];
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Detect which module we're in based on route
  const isModuleRoute = location.pathname.startsWith('/crm') ||
                        location.pathname.startsWith('/hrm') ||
                        location.pathname.startsWith('/finance');
  const Layout = isModuleRoute ? ModuleLayout : AdminLayout;

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch?.(value);
  };

  return (
    <Layout>
      <div className="min-h-full bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4 py-3">
            {/* Header Row */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {Icon && (
                  <div className={`w-9 h-9 bg-gradient-to-br ${colors.gradient} rounded-lg flex items-center justify-center`}>
                    <Icon size={18} className="text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-lg font-bold text-gray-900">{title}</h1>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    disabled={loading}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    title="Refresh data"
                  >
                    <RefreshCw size={16} className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                )}
                {showExport && onExport && canExport && (
                  <ExportDropdown onExport={onExport} loading={loading} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 space-y-4">

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Error loading report</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Filters Panel */}
          {showFilters && filters && (
            <FilterPanel isExpanded={filtersExpanded} onToggle={() => setFiltersExpanded(!filtersExpanded)}>
              {filters}
            </FilterPanel>
          )}

          {/* Summary Cards */}
          {summaryCards.length > 0 && (
  <div className={`grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6`}>
              {summaryCards.map((card, index) => (
                <SummaryCard key={index} {...card} color={color} />
              ))}
            </div>
          )}

          {/* Custom Content */}
          {children}

          {/* Data Table Section */}
          {columns.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Table Header with Search */}
              {showSearch && (
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearch}
                      placeholder={searchPlaceholder}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div className="text-sm text-gray-500">
                    {pagination?.totalItems ? `${pagination.totalItems.toLocaleString()} records` : ''}
                  </div>
                </div>
              )}

              {/* Table */}
              <DataTable
                columns={columns}
                data={data}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
                loading={loading}
              />

              {/* Pagination */}
              {pagination && (
                <div className="px-4 border-t border-gray-200">
                  <Pagination
                    totalPages={pagination.totalPages}
                    currentPage={pagination.currentPage}
                    onPageChange={pagination.onPageChange}
                    totalItems={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageSizeChange={pagination.onPageSizeChange}
                    pageSizeOptions={[25, 50, 100]}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

// Export all components
export { colorSchemes };
